import { useState, useEffect, useRef } from "react";
import { fetchOnce } from "./fetchOnce";

interface WidgetQueryState<T> {
  isLoading: boolean;
  data: T;
}

/**
 * Fetch hook for homepage widgets.
 * - Dedupes identical paths via fetchOnce
 * - Ignores stale responses when deps change or on unmount (Strict Mode safe)
 */
export const useWidgetQuery = <T>(
  path: string | null,
  appFetch: (url: string) => Promise<any>,
  select: (raw: unknown) => T,
  initialData: T,
): WidgetQueryState<T> => {
  const appFetchRef = useRef(appFetch);
  appFetchRef.current = appFetch;

  const selectRef = useRef(select);
  selectRef.current = select;

  const [state, setState] = useState<WidgetQueryState<T>>(() => ({
    isLoading: path !== null,
    data: initialData,
  }));

  const requestGenerationRef = useRef(0);

  useEffect(() => {
    if (path === null) {
      setState({ isLoading: false, data: initialData });
      return;
    }

    const generation = ++requestGenerationRef.current;
    setState((prev) => ({ ...prev, isLoading: true }));

    fetchOnce(path, () => appFetchRef.current(path))
      .then((raw) => selectRef.current(raw))
      .then((data) => {
        if (generation !== requestGenerationRef.current) return;
        setState({ isLoading: false, data });
      })
      .catch((error) => {
        if (generation !== requestGenerationRef.current) return;
        console.error(error);
        setState({ isLoading: false, data: initialData });
      });

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [path, initialData]);

  return state;
};

/**
 * Multi-step widget fetch with the same stale-response guard.
 */
export const useWidgetAsyncQuery = <T>(
  queryKey: string | null,
  queryFn: () => Promise<T>,
  initialData: T,
): WidgetQueryState<T> => {
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const [state, setState] = useState<WidgetQueryState<T>>(() => ({
    isLoading: queryKey !== null,
    data: initialData,
  }));

  const requestGenerationRef = useRef(0);

  useEffect(() => {
    if (queryKey === null) {
      setState({ isLoading: false, data: initialData });
      return;
    }

    const generation = ++requestGenerationRef.current;
    setState((prev) => ({ ...prev, isLoading: true }));

    queryFnRef
      .current()
      .then((data) => {
        if (generation !== requestGenerationRef.current) return;
        setState({ isLoading: false, data });
      })
      .catch((error) => {
        if (generation !== requestGenerationRef.current) return;
        console.error(error);
        setState({ isLoading: false, data: initialData });
      });

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [queryKey, initialData]);

  return state;
};
